import { useState, useEffect, useRef } from "preact/hooks";
import { UserMenu } from "./UserMenu";
import { Footer } from "./Footer";
import { useNavigate } from "../contexts/AppContext";

interface Portal {
  id: number;
  slug: string;
  title: string;
}

export function Home() {
  const navigate = useNavigate();
  const [portals, setPortals] = useState<Portal[]>([]);
  const [loading, setLoading] = useState(true);

  const handleDelete = async (slug: string) => {
    if (!confirm("Delete this page?")) return;
    try {
      const res = await fetch(`/api/portals/${encodeURIComponent(slug)}`, {
        method: "DELETE",
      });
      if (res.ok) {
        setPortals((prev) => prev.filter((p) => p.slug !== slug));
      } else {
        alert("Delete failed");
      }
    } catch {
      alert("Delete failed");
    }
  };

  const handleRename = async (slug: string) => {
    const newTitle = prompt("New name:");
    if (!newTitle || !newTitle.trim()) return;
    try {
      const res = await fetch(`/api/portals/${encodeURIComponent(slug)}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ title: newTitle.trim() }),
      });
      if (res.ok) {
        setPortals((prev) =>
          prev.map((p) =>
            p.slug === slug ? { ...p, title: newTitle.trim() } : p,
          ),
        );
      } else {
        alert("Rename failed");
      }
    } catch {
      alert("Rename failed");
    }
  };

  useEffect(() => {
    fetch("/api/portals")
      .then((r) => r.json())
      .then((data) => {
        setPortals(Array.isArray(data) ? data : []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const iframeWidth = 800;

  return (
    <div class="d-flex flex-column min-vh-100">
      <div class="container py-5 flex-grow-1">
        <div class="row justify-content-center">
          <div class="col-lg-8">
            <div class="d-flex justify-content-between align-items-center mb-5">
              <div>
                <h1 class="display-5 fw-bold mb-0">Your pages</h1>
              </div>
              <a
                class="btn btn-primary"
                href="/create"
                onClick={(e: Event) => {
                  e.preventDefault();
                  navigate("/create");
                }}
              >
                <i class="bi bi-plus-lg"></i> New page
              </a>
            </div>

            <div class="mb-4">
              <UserMenu />
            </div>

            {loading ? (
              <div class="d-flex justify-content-center py-5">
                <div class="spinner-border text-primary" role="status">
                  <span class="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : portals.length === 0 ? (
              <div class="card shadow-sm">
                <div class="card-body text-center py-5">
                  <h5 class="card-title">No pages yet</h5>
                  <p class="card-text text-muted">
                    Create your first page to get started.
                  </p>
                  <a
                    class="btn btn-primary"
                    href="/create"
                    onClick={(e: Event) => {
                      e.preventDefault();
                      navigate("/create");
                    }}
                  >
                    <i class="bi bi-plus-lg"></i> New page
                  </a>
                </div>
              </div>
            ) : (
              <div class="vstack gap-3">
                {portals.map((p) => (
                  <PortalCard
                    key={p.id}
                    portal={p}
                    iframeWidth={iframeWidth}
                    onRename={handleRename}
                    onDelete={handleDelete}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}

function PortalCard({
  portal,
  iframeWidth,
  onRename,
  onDelete,
}: {
  portal: Portal;
  iframeWidth: number;
  onRename: (slug: string) => void;
  onDelete: (slug: string) => void;
}) {
  const navigate = useNavigate();
  const previewRef = useRef<HTMLDivElement>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const { id, slug, title } = portal;

  // Measure the preview column and scale the 800px-wide iframe
  // to match — the column is col-5/col-sm-4, so its width varies
  // with viewport.
  useEffect(() => {
    const el = previewRef.current;
    const ifr = iframeRef.current;
    if (!el || !ifr) return;
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const w = entry.contentBoxSize?.[0]?.inlineSize ?? entry.contentRect.width;
        if (w > 0) {
          ifr.style.transform = `scale(${w / iframeWidth})`;
        }
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [iframeWidth]);

  return (
    <div class="card shadow-sm overflow-hidden">
      <div class="row g-0">
        <div ref={previewRef} class="col-5 col-sm-4">
          <div class="ratio ratio-4x3">
            <iframe
              ref={iframeRef}
              style={{
                width: `${iframeWidth}px`,
                height: "900px",
                pointerEvents: "none",
                transformOrigin: "top left",
              }}
              src={`/api/portals/${encodeURIComponent(id)}/preview`}
              loading="lazy"
              title={`Preview of ${title}`}
            />
          </div>
        </div>
        <div class="col-7 col-sm-8 d-flex">
          <div class="card-body vstack gap-2 py-2 px-3 justify-content-center w-100">
            <div class="h5 text-truncate mb-0">{title}</div>
            <div class="hstack gap-1 flex-wrap">
              <a
                class="btn btn-secondary btn-sm stretched-link"
                href={`/edit/${id}`}
                onClick={(e: Event) => {
                  e.preventDefault();
                  navigate(`/edit/${id}`);
                }}
              >
                <i class="bi bi-chat"></i> Chat
              </a>
              <button
                type="button"
                class="btn btn-outline-secondary btn-sm position-relative"
                style={{ zIndex: 2 }}
                onClick={(e: Event) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onRename(slug);
                }}
              >
                <i class="bi bi-pencil"></i> Rename
              </button>
              <button
                type="button"
                class="btn btn-outline-danger btn-sm border-0 position-relative"
                style={{ zIndex: 2 }}
                title="Delete"
                onClick={(e: Event) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onDelete(slug);
                }}
              >
                <i class="bi bi-trash"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
